import { useMemo, useState } from 'react';
import { PageHeader, Section } from '@/components/ops/Layout';

/** One term the console uses, said plainly. */
export interface GlossaryTerm {
  term: string;
  meaning: string;
  /** Other words people use for it (searchable). */
  aka?: string[];
}

export const GLOSSARY: GlossaryTerm[] = [
  { term: 'Tenant', meaning: 'One customer company on the platform, with its own users, data and settings.', aka: ['account', 'customer'] },
  { term: 'Environment', meaning: 'One running copy of the platform on its own servers — for example Tavi or NDC. A tenant lives on exactly one.' },
  { term: 'Saved copy', meaning: 'What the collectors last read from the platform, stored in the console so pages still work when the platform is down. Every figure from it says how old it is.', aka: ['replica'] },
  { term: 'Live', meaning: 'Read from the service at the moment the page asks. Money, credits and balances are always live; when the service is down the page says so instead of showing an old or zero figure.' },
  { term: 'Collector', meaning: 'A small scheduled job on each server that sends the console its numbers (tenants, users, disk, CPU). A missing sample shows as a gap, never as zero.' },
  { term: 'Measured / estimated', meaning: 'Measured figures were counted directly (database size, requests). Estimated ones are worked out from a share of the environment — like a tenant\'s CPU — and are always labelled so.' },
  { term: 'Setup job', meaning: 'The step-by-step creation of a tenant. Every step can be retried safely; nothing is set up twice.', aka: ['provisioning'] },
  { term: 'Setup check', meaning: 'Asks every service whether a tenant is fully set up, without changing anything — the same checks a setup job ends with.', aka: ['parity'] },
  { term: 'Industry', meaning: 'Starter content for a kind of business: CRM fields, a chat menu, ready replies, tags, e-mail templates, a board and sample records.', aka: ['industry pack'] },
  { term: 'Plan', meaning: 'What a tenant pays for: its apps, seat limit and monthly AI credits.' },
  { term: 'Seat limit', meaning: 'How many people a tenant may have. "Not limited" is a real setting, not zero.' },
  { term: 'Override', meaning: 'A by-hand exception for one tenant — an app granted or denied against its plan, or a special price. A "deny" always wins.' },
  { term: 'Credits', meaning: 'The unit a tenant spends on AI and on SMS/WhatsApp. Stored in tiny units (micro-credits) and shown with billing\'s display scale.' },
  { term: 'Wallet', meaning: 'Where a tenant\'s credits sit: one for AI, one for SMS and WhatsApp. "Held" credits are reserved for work in progress; "lent" credits are out with WhatsApp sending.' },
  { term: 'Price book', meaning: 'The published list of prices, versioned. A new version takes effect from its start date; old invoices keep the prices they were made with.' },
  { term: 'Offer', meaning: 'A discount, bonus credits or free months, for everyone or for chosen tenants or plans, within dates and limits.' },
  { term: 'Payment gateway', meaning: 'The company that takes card payments (Paymob). A "BYO" gateway is an external account\'s own merchant account, used only for its customers.', aka: ['BYO'] },
  { term: 'External account', meaning: 'A customer that uses our SMS/WhatsApp and payments APIs from its own system, without the CRM.' },
  { term: 'Sender ID', meaning: 'The name an SMS shows as coming from. Operators must approve it before it can be used.' },
  { term: 'DLR', meaning: 'Delivery report: the operator telling us whether an SMS reached the phone.', aka: ['delivery report'] },
  { term: 'Retention', meaning: 'How long each kind of data is kept before it is deleted automatically. Invoices are never deleted.' },
  { term: 'Health score', meaning: 'A 0–100 summary of how well a tenant is using the product, with how much of it could be measured. "n/a" means nothing measurable yet — not a bad score.' },
  { term: 'At risk', meaning: 'Tenants showing warning signs (no sign-ins, unused seats, falling usage), ordered by what they pay.' },
  { term: 'MRR / plan revenue', meaning: 'Monthly revenue from the plans on record (annual plans divided by twelve). Internal tenants are not counted.' },
  { term: 'Internal tenant', meaning: 'One of our own (demo, staging, testing) — kept out of customer counts by default.' },
  { term: 'Console admin', meaning: 'A person who can sign in to this console. Owners can do everything; analysts read everything except the admin list, export, and work leads; viewers read dashboards, invoices, messaging and servers — not plans, costs, names of people or exports.' },
];

/** Terms matching a search, in their order; the search looks at the term, its other names and its meaning. */
export function searchGlossary(query: string, terms: GlossaryTerm[] = GLOSSARY): GlossaryTerm[] {
  const q = query.trim().toLowerCase();
  if (!q) return terms;
  return terms.filter((t) =>
    t.term.toLowerCase().includes(q) || (t.aka ?? []).some((a) => a.toLowerCase().includes(q)) || t.meaning.toLowerCase().includes(q));
}

/** Plain meanings for the words the console uses. */
export function GlossaryPage() {
  const [query, setQuery] = useState('');
  const terms = useMemo(() => searchGlossary(query), [query]);

  return (
    <>
      <PageHeader title="Glossary" subtitle="What the words on these pages mean." />
      <div className="filterbar ops-filters">
        <input className="input" style={{ maxWidth: 320 }} placeholder="Search a word" aria-label="Search the glossary"
          value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <Section title={`${terms.length} term${terms.length === 1 ? '' : 's'}`}>
        {terms.length === 0 ? (
          <p className="t-body muted" style={{ margin: 0 }}>No term matches “{query}”.</p>
        ) : (
          <dl className="glossary">
            {terms.map((t) => (
              <div key={t.term} className="glossary-row">
                <dt className="t-heading">{t.term}{t.aka?.length ? <span className="t-caption muted"> · also {t.aka.join(', ')}</span> : null}</dt>
                <dd className="t-body">{t.meaning}</dd>
              </div>
            ))}
          </dl>
        )}
      </Section>
    </>
  );
}
